import { Link } from "react-router-dom";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { PayslipType } from "types/payroll-types";
import TableHeader from "components/TableHeader";
import TableCell from "components/TableCell";
import Table from "components/Table";
import { formatAmount } from "utils/utilityFunctions";
import { useNavigate } from "react-router-dom";
import { PaginatedResponse } from "types/payroll-types";

interface PayslipsTableType {
  tableData: PayslipType[],
  tableName: string,
  paginationData?: PaginatedResponse<PayslipType>
  currentPage?: number
  pageSize?: number
  getMyPayslipsPage?: (pageUrl: string, action: string) => Promise<void>;
  onPageSizeChange?: (size: number) => void;
  ownPayslips?: boolean,
}

function PayslipsTable(props: PayslipsTableType) {
  const { 
    tableData, 
    tableName, 
    paginationData, 
    currentPage, 
    pageSize,
    getMyPayslipsPage,
    onPageSizeChange,
    ownPayslips 
  } = props;

  const columnHelper = createColumnHelper<PayslipType>();
  const navigate = useNavigate();
  
  const onRowClick = (payslip: PayslipType) => {
    navigate(ownPayslips ? `/my-payslip/${payslip.id}` : `/payslip/${payslip.id}`);
  }
  
  const columns: ColumnDef<PayslipType, any>[] = [
    columnHelper.accessor("id", {
      id: "id",
      header: () => (
        <TableHeader>ID</TableHeader>
      ),
      cell: (info) => (
        <TableCell>
          {info.getValue()}
        </TableCell>
      ),
    }),
    columnHelper.accessor("employee_details.first_name", {
      id: "first_name",
      header: () => (
        <TableHeader>First Name</TableHeader>
      ),
      cell: (info) => (
        <TableCell>
          {info.getValue()}
        </TableCell>
      ),
    }),
    columnHelper.accessor("employee_details.last_name", {
      id: "last_name",
      header: () => (
        <TableHeader>Last Name</TableHeader>
      ),
      cell: (info) => (
        <TableCell>
          {info.getValue()}
        </TableCell>
      ),
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => (
        <TableHeader>Status</TableHeader>
      ),
      cell: (info) => (
        <TableCell>
          {info.getValue()}
        </TableCell>
      ),
    }),
    columnHelper.accessor("net_salary", {
      id: "net_salary",
      header: () => (
        <TableHeader>Net Salary</TableHeader>
      ),
      cell: (info) => (
        <TableCell>
          {formatAmount(info.getValue())}
        </TableCell>
      ),
    }),
    columnHelper.accessor("period_start", {
      id: "period_start",
      header: () => (
        <TableHeader>Period Start</TableHeader>
      ),
      cell: (info) => (
        <TableCell>
          {info.getValue()}
        </TableCell>
      ),
    }),
    columnHelper.accessor("period_end", {
      id: "period_end",
      header: () => (
        <TableHeader>Period End</TableHeader>
      ),
      cell: (info) => (
        <TableCell>
          {info.getValue()}
        </TableCell>
      ),
    }),
  ];

  const handleNextPage = () => {
    if (paginationData?.next) {
      getMyPayslipsPage?.(paginationData.next, "next");
    }
  };

  const handlePreviousPage = () => {
    if (paginationData?.previous) {
      getMyPayslipsPage?.(paginationData.previous, "previous");
    }
  };

  return (
    <Table<PayslipType> 
      tableData={tableData} 
      columns={columns} 
      tableHeading={tableName} 
      onRowClick={onRowClick}
      totalCount={paginationData?.count}
      currentPage={currentPage}
      pageSize={pageSize}
      hasNextPage={!!paginationData?.next}
      hasPreviousPage={!!paginationData?.previous}
      onNextPage={handleNextPage}
      onPreviousPage={handlePreviousPage}
      onPageSizeChange={onPageSizeChange}
    />
  );
}

export default PayslipsTable;
